import { z } from 'zod';
import type { Brand } from '@/models';
import { getBrandLogoType } from '@/lib/brand-logos';
import { getPublicSupabaseClient } from './supabase-client';

export interface BrandRepository {
  list(): Promise<Brand[]>;
  findBySlug(slug: string): Promise<Brand | null>;
}

const brandRowSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name_en: z.string(),
  name_ar: z.string(),
  logo_url: z.string().nullable().optional(),
});

const DEFAULT_ACCENT_COLOR = '#2563eb';

function toBrand(row: z.infer<typeof brandRowSchema>): Brand {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name_en,
    nameAr: row.name_ar,
    logoType: getBrandLogoType(row.slug),
    accentColor: DEFAULT_ACCENT_COLOR,
    logoUrl: row.logo_url ?? undefined,
  };
}

export const supabaseBrandRepository: BrandRepository = {
  async list() {
    const supabase = await getPublicSupabaseClient();
    if (!supabase) {
      return [];
    }

    const { data, error } = await supabase
      .from('brands')
      .select('id, slug, name_en, name_ar, logo_url')
      .order('display_order', { ascending: true });

    if (error) {
      console.warn('Failed to load brands from Supabase:', error.message);
      return [];
    }

    return (data ?? []).flatMap((row) => {
      const parsed = brandRowSchema.safeParse(row);
      return parsed.success ? [toBrand(parsed.data)] : [];
    });
  },

  async findBySlug(slug) {
    const supabase = await getPublicSupabaseClient();
    if (!supabase) {
      return null;
    }

    const { data, error } = await supabase
      .from('brands')
      .select('id, slug, name_en, name_ar, logo_url')
      .eq('slug', slug)
      .maybeSingle();

    if (error || !data) {
      return null;
    }

    const parsed = brandRowSchema.safeParse(data);
    return parsed.success ? toBrand(parsed.data) : null;
  },
};
